// frontend/src/components/BlogSelectModal.js
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useBlog } from '../context/BlogContext';
import { useThemeContext } from '../context/ThemeContext';
import { useUser } from '../context/UserContext';
import { useAuth } from '../context/AuthContext';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.card || 'white'};
  padding: 30px;
  border-radius: 16px;
  max-width: 500px;
  width: 90%;
  text-align: center;
  animation: fadeIn 0.3s ease-in-out;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  font-family: ${({ theme }) => theme.font || 'inherit'};

  @keyframes fadeIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  margin: 20px 0;
  border-radius: 8px;
  font-size: 1rem;
  border: 1px solid ${({ theme }) => theme.inputBorder || '#ccc'};
  background: ${({ theme }) => theme.inputBackground || '#f9f9f9'};
  color: ${({ theme }) => theme.text || '#000'};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary || '#2cc2d4'};
    background: white;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  background-color: ${({ theme }) => theme.primary || '#2cc2d4'};
  color: ${({ theme }) => theme.buttonText || 'white'};
  font-weight: 600;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: ${({ theme }) => theme.primaryHover || '#239db0'};
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const Error = styled.div`
  color: red;
  font-weight: bold;
  margin-top: 10px;
`;

const BlogSelectModal = ({ onClose, onSelect }) => {
  const { blogs, loading } = useUser();
  const { selectBlog } = useBlog();
  const { theme } = useThemeContext();
  const { refreshBlogAuthz } = useAuth();

  const [selectedSlug, setSelectedSlug] = useState('');
  const [error, setError] = useState(null);

  // constants / helpers
  const roleLabel = {
    owner: "Owner",
    admin: "Admin",
    editor: "Editor",
    viewer: "Viewer",
  };

  const pick = async (blog) => {
    setError(null);
    selectBlog(blog);              // sets localStorage + X-Blog-ID
    await refreshBlogAuthz();      // fetches role/perms for selected blog
    onSelect?.(blog);
    onClose?.();
  };

  useEffect(() => {
    if (loading) return;
    if (blogs.length === 1) {
      pick(blogs[0]).catch((err) => {
        console.error('Auto-select blog failed:', err);
        setError('Unable to select blog.');
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, blogs]);

  const handleSelect = async () => {
    const blog = blogs.find((b) => b.slug === selectedSlug);
    if (!blog) return setError('Please select a blog.');

    try {
      console.log("BlogSelectModal picked blog:", blog);
      await pick(blog);
    } catch (err) {
      console.error('Select blog failed:', err);
      setError('Unable to select blog.');
    }
  };

  return (
    <ModalOverlay>
      <ModalContent theme={theme}>
        <h2>Select a Blog</h2>
        {error && <Error>{error}</Error>}

        {loading ? (
          <p>Loading blogs...</p>
        ) : blogs.length === 0 ? (
          <p>No blogs found for this user.</p>
        ) : (
          <>
            <Select
              value={selectedSlug}
              onChange={(e) => setSelectedSlug(e.target.value)}
              theme={theme}
            >
              <option value="" disabled>Select a blog</option>
              {blogs.map((b) => (
                <option key={b.slug} value={b.slug}>
                  {b.title}{b.role ? ` (${roleLabel[b.role] ?? b.role})` : ""}
                </option>
              ))}
            </Select>

            <Button theme={theme} onClick={handleSelect} disabled={!selectedSlug}>
              Continue
            </Button>
          </>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};

export default BlogSelectModal;
